import '../../../styles/restScreens.css';
import { useState } from 'react';
import { supabaseClient } from '../../../config/supabase';
import { toneTextStyle } from '../clientsConstants';
import { buildImportRows, guessMapping, parseCsv } from '../../../lib/contactCsv';
import { parseVcard } from '../../../lib/vcard';
import { runPersonalImport } from '../../../lib/personalContacts';

import { BTN } from '../../ui/platformStandard';
// BL-177 PR D4: import into the signed-in person's OWN My Contacts from a CSV (PR C's parser and column guesser) or a vCard (.vcf).
// ALWAYS a preview first (the same database function, rolled back); nothing is written until the person clicks Import. Never overwrites:
// a person already in the list gets empty fields filled and extra phones/emails added; conflicts are listed.
const readFileText = (file) => (typeof file.text === 'function'
    ? file.text()
    : new Promise((resolve, reject) => { const reader = new FileReader(); reader.onload = () => resolve(String(reader.result)); reader.onerror = reject; reader.readAsText(file); }));

const field = 'rounded border border-[var(--surface-border)] bg-[var(--surface-sunken)] px-2 py-1.5 text-sm text-[var(--ink)] outline-none focus:border-brand-500';
const COUNT_LABELS = [
    ['rows_read', 'Rows read'], ['new_people', 'New contacts'], ['matched', 'Matched to your existing contacts'],
    ['extra_phones_added', 'Extra phones added'], ['extra_emails_added', 'Extra emails added'],
    ['conflict_count', 'Conflicts (kept as is)'], ['invalid_count', 'Invalid numbers / emails'], ['skipped', 'Skipped'],
];

export default function PersonalImportPanel({ onClose, onDone, client = supabaseClient }) {
    const [filename, setFilename] = useState('');
    const [kind, setKind] = useState(null);           // 'csv' | 'vcard'
    const [parsed, setParsed] = useState(null);
    const [mapping, setMapping] = useState(null);
    const [vcardRows, setVcardRows] = useState([]);
    const [preview, setPreview] = useState(null);
    const [result, setResult] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    const onFile = async (event) => {
        const file = event.target.files?.[0];
        setPreview(null); setResult(null); setError(null);
        if (!file) return;
        const text = await readFileText(file);
        setFilename(file.name);
        if (/\.vcf$/i.test(file.name) || /BEGIN:VCARD/i.test(text.slice(0, 200))) {
            setKind('vcard'); setParsed(null); setMapping(null); setVcardRows(parseVcard(text));
        } else {
            const csv = parseCsv(text);
            setKind('csv'); setVcardRows([]); setParsed(csv); setMapping(guessMapping(csv.headers));
        }
    };

    const rows = kind === 'vcard' ? vcardRows : (parsed && mapping ? buildImportRows(parsed, mapping) : []);
    const run = async (apply) => {
        setBusy(true); setError(null);
        try {
            const data = await runPersonalImport(client, { rows, apply, source: kind === 'vcard' ? 'vCard import' : 'CSV import' });
            if (apply) { setResult(data); setPreview(null); onDone?.(); } else setPreview(data);
        } catch (err) { setError(err.message || String(err)); } finally { setBusy(false); }
    };
    const set = (key, value) => setMapping(m => ({ ...m, [key]: value }));
    const shown = result || preview;
    const headers = parsed?.headers || [];

    return (
        <div data-m6-layout="clients" data-m6-component="PersonalImportPanel" className="mb-4 rounded-lg border border-[var(--surface-border)] bg-[var(--surface-raised)] p-4 shadow-sm" data-testid="personal-import-panel">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <div className="text-sm font-semibold text-[var(--ink)]">Import into My Contacts</div>
                    <p className="text-xs text-[var(--ink-muted)]">A CSV or vCard (.vcf) file. It goes into YOUR list only - nobody else's, and never the company list. You see a preview first; nothing is saved until you click Import.</p>
                </div>
                <button type="button" onClick={onClose} className="text-sm text-[var(--ink-muted)] hover:text-[var(--ink)]" aria-label="Close import">×</button>
            </div>

            <input type="file" accept=".csv,.vcf,text/csv,text/vcard" aria-label="Contacts file" onChange={onFile} className="mt-3 block w-full max-w-full text-sm text-[var(--ink)]" />

            {kind === 'csv' && mapping && (
                <div className="mt-3 grid gap-2 sm:grid-cols-3" data-testid="personal-import-mapping">
                    {[['name', 'Name'], ['account_name', 'Company'], ['title', 'Position']].map(([key, text]) => (
                        <label key={key} className="flex flex-col gap-1 text-xs text-[var(--ink-muted)]">
                            {text}
                            <select className={field} aria-label={`${text} column`} value={mapping[key]} onChange={event => set(key, event.target.value)}>
                                <option value="">— not in this file —</option>
                                {headers.map(h => <option key={h} value={h}>{h}</option>)}
                            </select>
                        </label>
                    ))}
                    <div className="text-xs text-[var(--ink-muted)] sm:col-span-3">
                        Phones found in: {mapping.phones.map(p => p.column).join(', ') || 'none'} · Emails found in: {mapping.emails.map(p => p.column).join(', ') || 'none'}
                    </div>
                </div>
            )}
            {kind && <div className="mt-2 text-xs text-[var(--ink-muted)]">{filename}: {rows.length} row{rows.length === 1 ? '' : 's'} read.</div>}

            {shown && (
                <dl className="mt-3 grid grid-cols-2 gap-x-6 gap-y-1 text-sm sm:grid-cols-4" data-testid="personal-import-counts">
                    {COUNT_LABELS.map(([key, text]) => (
                        <div key={key}><dt className="text-xs text-[var(--ink-muted)]">{text}</dt><dd className="font-semibold text-[var(--ink)]">{shown[key] ?? 0}</dd></div>
                    ))}
                </dl>
            )}
            {result && <div role="status" className="mt-2 text-sm" style={toneTextStyle('good')}>Imported into My Contacts.</div>}
            {error && <div role="alert" className="mt-2 text-sm" style={toneTextStyle('alert')}>{error}</div>}

            <div className="mt-3 flex gap-2">
                <button type="button" disabled={!rows.length || busy} onClick={() => run(false)}
                    className={BTN}>Preview</button>
                <button type="button" disabled={!preview || busy || !!result} onClick={() => run(true)}
                    className={BTN}>Import</button>
            </div>
        </div>
    );
}
